<template lang="html">
  <div class="event-code-selector">
    <slot name="settings"></slot>
    <table class="codes-table">
      <thead>
        <tr>
          <th :style="{ width: (source === 'node' || source === 'zone') ? '200px' : '250px' }">
            <CheckBox v-model="includeAll" />
            {{ $txt.include_all }}
          </th>
          <th v-if="source == 'node' || source == 'zone'" style="width: 50px;"></th>
          <th style="width: 250px;">
            <CheckBox v-model="includeAdvancedLocal" />
            {{ $txt.show_advanced_events }}
          </th>
          <th style="width: 250px;" v-if="source === 'node'"></th>
          <th style="width: 250px;" v-if="source == 'cntrl' || source == 'project'"></th>
          <th style="width: 250px;" v-if="source == 'zone' || source == 'cntrl' || source == 'project'"></th>
          <th style="width: 250px;"></th>
          <th v-if="source == 'node' || source == 'zone'" style="width: 200px;"></th>
        </tr>
      </thead>
    </table>
    <table class="codes-table">
      <tbody>
        <tr>
          <td style="width: 250px;" v-if="source == 'cntrl' || source == 'project'">
            <!-- Controller Column -->
            <div class="code_band">
              <div class="subheader">
                <CheckBox v-model="allControllerCodesIncluded" />
                {{ $txt.controller }}
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'controller')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>

            <!-- Zone Column -->
            <div class="code_band">
              <div class="subheader">
                <CheckBox v-model="allZoneCodesIncluded" />
                {{ $txt.zone }}
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'zone')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>
          </td>
          <td style="width: 250px;" v-if="source == 'zone'">
            <!-- Zone Column -->
            <div class="code_band">
              <div class="subheader">
                <CheckBox v-model="allZoneCodesIncluded" />
                Zone
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'zone')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>
          </td>
          <td style="width: 250px;">
            <!-- Node Column -->
            <div class="code_band">
              <div class="subheader">
                <CheckBox v-model="allNodeCodesIncluded" />
                {{ $txt.holder }}
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'node')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>

            <!-- Channels Column -->
            <div class="code_band" v-if="source == 'cntrl' || source == 'project'">
              <div class="subheader">
                <CheckBox v-model="allChannelCodesIncluded" />
                {{ $txt.channel }}
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'channel')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>
          </td>
          <td style="width: 250px;" v-if="source == 'node' || source == 'zone'">
            <!-- Channels Column -->
            <div class="code_band">
              <div class="subheader">
                <CheckBox v-model="allChannelCodesIncluded" />
                Channel
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'channel')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>
          </td>
          <td style="width: 250px;">
            <!-- Communication Column -->
            <div class="code_band" v-if="hasEventCodesFromCategory('communication')">
              <div class="subheader">
                <CheckBox v-model="allCommunicationCodesIncluded" />
                {{ $txt.notify_category_comm }}
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'communication')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>

            <!-- Recording Column -->
            <div class="code_band" v-if="hasEventCodesFromCategory('recording')">
              <div class="subheader">
                <CheckBox v-model="allRecordingCodesIncluded" />
                {{ $txt.recording }}
              </div>
              <div v-for="code in selectedCodes.filter(code => code.category === 'recording')" :key="code.id">
                <CheckBox v-model="code.to_include" />
                {{ code_label(code) }}
              </div>
            </div>
          </td>
          <td style="width: 250px;">
            <!-- Other Column -->
            <div class="code_band" v-if="hasEventCodesFromCategory('other')">
              <div class="subheader">
                <CheckBox v-model="allOtherCodesIncluded" />
                {{ $txt.other }}
              </div>
              <div class="long-list">
                <div v-for="code in selectedCodes.filter(code => code.category === 'other')" :key="code.id">
                  <CheckBox v-model="code.to_include" />
                  {{ code_label(code) }}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
// Reusable event-code selection grid.
//
// Presents the categorized code checkboxes (mirrors the Events tab), driven by
// `source` (node/zone/cntrl/project). Persistence is intentionally left to the
// parent: the include state is exposed via v-model as a { codeId: bool } map,
// and the currently-visible included ids are emitted via @change so the parent
// can build its fetch filter. EventsTab persists the map to localStorage; the
// report persists it on the report layout.
import globalSettings from "@/globalsettings.js";

const VISIBILITY_MAP = {
  node: ["node"],
  zone: ["node", "zone"],
  cntrl: ["node", "zone", "cntrl"],
  project: ["node", "zone", "cntrl", "project"],
};

export default {
  name: "EventCodeSelector",
  props: {
    source: { type: String, default: "project" },
    // include map: { [codeId]: boolean }. v-model.
    modelValue: { type: Object, default: () => ({}) },
    // whether advanced codes are shown. v-model:includeAdvanced.
    includeAdvanced: { type: Boolean, default: true },
  },
  emits: ["update:modelValue", "update:includeAdvanced", "change"],
  data() {
    return {
      selectedCodes: [],
    };
  },
  created() {
    this.rebuild();
  },
  watch: {
    source() {
      this.rebuild();
    },
    includeAdvanced() {
      this.rebuild();
    },
    selectedCodes: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
  },
  computed: {
    includeAdvancedLocal: {
      get() {
        return this.includeAdvanced;
      },
      set(value) {
        this.$emit("update:includeAdvanced", value);
      },
    },
    includeAll: {
      get() {
        return this.selectedCodes.length > 0 && this.selectedCodes.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { code.to_include = value; });
      },
    },
    allControllerCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'controller');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'controller') code.to_include = value; });
      },
    },
    allZoneCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'zone');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'zone') code.to_include = value; });
      },
    },
    allNodeCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'node');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'node') code.to_include = value; });
      },
    },
    allChannelCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'channel');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'channel') code.to_include = value; });
      },
    },
    allRecordingCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'recording');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'recording') code.to_include = value; });
      },
    },
    allCommunicationCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'communication');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'communication') code.to_include = value; });
      },
    },
    allOtherCodesIncluded: {
      get() {
        const c = this.selectedCodes.filter(code => code.category === 'other');
        return c.length > 0 && c.every(code => code.to_include);
      },
      set(value) {
        this.selectedCodes.forEach(code => { if (code.category === 'other') code.to_include = value; });
      },
    },
  },
  methods: {
    // Adds the event code to the label when the user has internal ids switched on -
    // the same setting that puts ids in the view titles and the id columns.
    code_label(code) {
      if (!this.$app.settings.display_internal_id) return code.name;
      return code.name + " (" + code.id + ")";
    },
    hasEventCodesFromCategory(category) {
      return this.selectedCodes.some(code => code.category === category);
    },
    // Build the visible code list from globalSettings for this source, applying
    // the include state from the incoming map (default include-all when unset).
    rebuild() {
      const allowed = VISIBILITY_MAP[this.source] || VISIBILITY_MAP.project;
      const map = this.modelValue || {};
      this.selectedCodes = globalSettings.selectedCodes
        .filter(code => {
          if (!allowed.includes(code.source)) return false;
          if (!this.includeAdvanced && code.advanced) return false;
          return true;
        })
        .map(code => ({
          ...code,
          to_include: Object.prototype.hasOwnProperty.call(map, code.id) ? !!map[code.id] : true,
        }));
    },
    // Merge current visible selections into a copy of the incoming map (so
    // values for codes not currently visible — e.g. hidden advanced ones — are
    // preserved) and emit both the map and the visible included ids.
    emitChange() {
      const map = { ...(this.modelValue || {}) };
      this.selectedCodes.forEach(code => { map[code.id] = !!code.to_include; });
      const includedCodeIds = this.selectedCodes.filter(code => code.to_include).map(code => code.id);
      this.$emit("update:modelValue", map);
      this.$emit("change", { map, includedCodeIds });
    },
  },
};
</script>

<style scoped>
.codes-table {
  width: 100%;
  border-collapse: collapse;
}
.codes-table th {
  text-align: left;
  padding: 5px;
  border-bottom: 1px solid #ccc;
}
.codes-table td {
  vertical-align: top;
  padding: 5px;
}
.codes-table td div {
  margin-bottom: 5px;
}
.long-list {
  column-count: 1;
  column-gap: 1px;
  width: 400px;
}
.codes-table .code_band:not(:last-child) {
  /* Kept inside the band, not between bands, so the hand-off from one heading
     to the next leaves no gap with no heading showing. */
  padding-bottom: 30px;
}

.codes-table .subheader {
  display: block;
  position: sticky;
  top: calc(var(--wm_sticky_offset, 0px) - var(--wm_sticky_pad, 0px) + var(--wm_section_stack, 0px));
  z-index: 2;
  font-weight: bold;
  border-bottom: 1px solid #ccc;
  padding: 5px;
  margin: 0 -5px 5px -5px;
  background-color: rgb(235, 235, 235);
}
</style>
